import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft } from '@gravity-ui/icons';
import { Tooltip } from '@gravity-ui/uikit';
import { PaginationItem } from '@mui/material';
import type { MRT_ColumnDef, MRT_PaginationState, MRT_SortingState } from 'material-react-table';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { EDIT_BLANK } from '../constants';

import { EstablishmentEditModal } from './modals/establishment-edit-modal';
import { Localization_RU } from './localization';

import { ReactComponent as ArrowsDownFilter } from '../../../assets/icons/arrow-down-filter.svg';
import { ReactComponent as ArrowsFilter } from '../../../assets/icons/arrows-filter.svg';

import styles from './styles.module.scss';

interface DataProps {
	data: Establishment[];
	onAdd: () => void;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	totalItems: number | undefined;
	sorting: MRT_SortingState;
	setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
}

interface Establishment {
	id?: number;
	name: string;
}

export const AdminTable = ({
	data,
	onAdd,
	pageSize,
	setPageSize,
	pageIndex,
	setPageIndex,
	totalItems,
	sorting,
	setSorting,
}: DataProps) => {
	const columns = useMemo<MRT_ColumnDef<Establishment>[]>(
		() => [
			{
				accessorKey: 'name',
				header: 'Учреждение',
				enableHiding: false,
			},
		],
		[],
	);

	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: pageIndex,
		pageSize: pageSize,
	});

	useEffect(() => {
		setPageSize(pagination.pageSize);
		setPageIndex(pagination.pageIndex);
	}, [pagination.pageSize, pagination.pageIndex, setPageSize, setPageIndex]);

	const table = useMaterialReactTable({
		columns,
		data, //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
		manualPagination: true,
		manualSorting: true,
		rowCount: totalItems,
		icons: {
			SyncAltIcon: (props: any) => <ArrowsFilter {...props} />,
			ArrowDownwardIcon: (props: any) => <ArrowsDownFilter {...props} />,
		},
		enableRowSelection: false, //enable some features
		enableTopToolbar: false,
		enableColumnActions: false, // отключить доп меню колонок
		enableRowActions: true, // наличие кнопок действия в строках
		positionActionsColumn: 'last',
		enableFilters: false, // доп фильтрация
		enableColumnOrdering: true,
		enableColumnDragging: false,
		enableGlobalFilter: false, //глобальная фильтрация
		localization: Localization_RU, //локализация
		enableStickyHeader: true, //липкий header
		enableStickyFooter: false, // липкий footer
		enableFullScreenToggle: false, // скрыть кнопку "на весь экран"
		positionPagination: 'both',
		state: { pagination, sorting },
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		muiPaginationProps: {
			boundaryCount: 3,
			rowsPerPageOptions: [20, 40, 60, 80, 100],
			shape: 'rounded',
			variant: 'text',
			showLastButton: false,
			sx: {
				paddingRight: '20px',
			},
			size: 'large',
			renderItem: (item) => (
				<PaginationItem slots={{ previous: ChevronLeft, next: ChevronRight, first: ChevronsLeft }} {...item} />
			),
		},
		initialState: {
			density: 'compact',
			pagination: {
				pageIndex: 0,
				pageSize: 20,
			},
		},
		paginationDisplayMode: 'pages',
		layoutMode: 'semantic',
		muiTableBodyCellProps: {
			sx: {
				p: '6px 8px 5px',
			},
		},
		muiTableBodyRowProps: {
			sx: {
				minHeight: '48px',
			},
		},
		muiTablePaperProps: {
			elevation: 0,
		},
		renderRowActions: ({ row }) => (
			<div className={styles.blankTable__action}>
				<Tooltip
					className={styles.blankTable__action__tooltip}
					content={EDIT_BLANK}
					placement="top"
					openDelay={500}
				>
					<div tabIndex={0}>
						<EstablishmentEditModal establishment={row.original} onAdd={onAdd} key={row.original.id} />
					</div>
				</Tooltip>
			</div>
		),
	});

	return <MaterialReactTable table={table} />;
};
